import * as React from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { CustomField, CustomSection, QuestionnaireResponse, TemplateResponse } from '@gcv/shared';
import { Box } from '@mui/material';

import { Header, Text } from 'ui';
import palette from 'ui/theme/palette';
import { shouldDisplayFieldWithSmartRule } from './util/should-display-field-with-smart-rule';

export const CustomFormGeneratorPreview: React.FC<{
  filteredSortedRequirements: string[] | undefined;
  requirement: QuestionnaireResponse | TemplateResponse;
  form: UseFormReturn<FieldValues>;
  isMobile: boolean;
  fieldAnimation: (field: CustomField | CustomSection) => '1.5s linear SMART_RULE_ANIM' | undefined;
  resetFieldValue: (fieldId: string) => void;
  getField: (
    field: CustomField,
    requirementId: string,
    requirementResultId?: string,
    repeatable?: boolean
  ) => JSX.Element | undefined;
  fieldWidth?: string | { xs?: string; sm?: string; md?: string; lg?: string };
}> = (props) => {
  return (
    <>
      {props.filteredSortedRequirements?.map((sectionId) => {
        const requirement = props.requirement.custom_requirements[sectionId];

        if (shouldDisplayFieldWithSmartRule(requirement.custom_section.smart_rule, props.form.getValues())) {
          return (
            <Box
              padding="0"
              width="100%"
              data-cy={`form-section-${requirement.custom_section.id}`}
              key={requirement.custom_section.id}
            >
              <Box
                sx={{
                  width: '100%',
                  WebkitAnimation: props.fieldAnimation(requirement.custom_section),
                  MozAnimation: props.fieldAnimation(requirement.custom_section),
                  OAnimation: props.fieldAnimation(requirement.custom_section),
                  animation: props.fieldAnimation(requirement.custom_section),
                  animationIterationCount: 1,
                  '@-webkit-keyframes SMART_RULE_ANIM': {
                    from: {
                      backgroundColor: '#FFFCE6'
                    },
                    to: {
                      backgroundColor: 'white'
                    }
                  }
                }}
              >
                <Box data-cy={requirement.id}>
                  <Header
                    mobileHeader={props.isMobile}
                    content={
                      requirement.custom_section.repeatable
                        ? `${requirement.custom_section.label} ${1}`
                        : requirement.custom_section.label
                    }
                    type={'h2'}
                  />
                </Box>

                {requirement.custom_section.description && (
                  <Box padding={'0 8px 8px'}>
                    <Text
                      sx={{ whiteSpace: 'pre-line' }}
                      content={requirement.custom_section.description}
                      type="body2"
                    />
                  </Box>
                )}
              </Box>
              {requirement.custom_section.fields.map((item) => {
                if (shouldDisplayFieldWithSmartRule(item.smart_rule, props.form.getValues())) {
                  return (
                    <Box
                      sx={{
                        width: '100%',
                        WebkitAnimation: props.fieldAnimation(item),
                        MozAnimation: props.fieldAnimation(item),
                        OAnimation: props.fieldAnimation(item),
                        animation: props.fieldAnimation(item),
                        animationIterationCount: 1,
                        '@-webkit-keyframes SMART_RULE_ANIM': {
                          from: {
                            backgroundColor: '#FFFCE6'
                          },
                          to: {
                            backgroundColor: 'white'
                          }
                        }
                      }}
                      key={item.id}
                    >
                      <Box data-cy="custom-section" width={props.fieldWidth}>
                        <Box
                          sx={{
                            mb: '0.5rem'
                          }}
                        >
                          {props.getField(item, sectionId)}
                        </Box>

                        {item.helpText && (
                          <Box>
                            <Text
                              content={item.helpText}
                              sx={{
                                color: palette.text.secondary,
                                fontFamily: 'Lato',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                fontSize: '13px',
                                lineHeight: '18.2px'
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                }
                props.resetFieldValue(item.id);
                return null;
              })}
            </Box>
          );
        } else {
          return null;
        }
      })}
    </>
  );
};
